import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import MuiCheckbox, {
  checkboxClasses,
  CheckboxProps,
} from '@mui/material/Checkbox';

import CheckedSvg from './checked.svg';

const CheckboxContainer = styled.label<{
  disabled: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  cursor: pointer;
  color: ${({ theme }) => theme.color.onYellowFixed};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};

  white-space: nowrap;

  ${({ theme, disabled }) =>
    disabled
      ? css`
          .checkmark {
            :after {
              opacity: ${theme.checkbox.unselected.disabled.stateLayer.opacity};
            }
          }
        `
      : css`
          :hover {
            .checkmark {
              outline: 4px solid
                ${theme.checkbox.unselected.hover.container.boxShadow};

              border-width: ${theme.checkbox.unselected.hover.container
                .borderWidth};

              :after {
                opacity: ${theme.checkbox.unselected.hover.stateLayer.opacity};
              }
            }
          }

          :active {
            .checkmark {
              border-radius: ${theme.checkbox.unselected.pressed.container
                .borderRadius};

              :after {
                border-radius: ${theme.checkbox.unselected.pressed.container
                  .borderRadius};
              }
            }
          }
        `}
`;

const ControlContainer = styled.div`
  position: relative;

  svg {
    width: 14px;
    height: 12px;
  }
`;

const Control = styled(MuiCheckbox)`
  &.${checkboxClasses.root} {
    padding: 0;
  }
`;

const UncheckedIcon = styled.span<{ disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  border-radius: ${({ theme }) =>
    theme.checkbox.unselected.enabled.container.borderRadius};
  border-color: ${({ theme }) =>
    theme.checkbox.unselected.enabled.container.borderColor};
  border-width: ${({ theme, disabled }) =>
    disabled ? '0px' : theme.checkbox.unselected.enabled.container.borderWidth};
  border-style: solid;
  background: ${({ theme }) =>
    theme.checkbox.unselected.enabled.container.fill};
  height: ${({ theme }) => theme.checkbox.unselected.enabled.container.height};
  width: ${({ theme }) => theme.checkbox.unselected.enabled.container.width};

  :after {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0;
    background: ${({ theme }) =>
      theme.checkbox.unselected.enabled.stateLayer.fill};
    border-radius: ${({ theme }) =>
      theme.checkbox.unselected.enabled.container.borderRadius};
  }
`;

const CheckedIcon = styled(UncheckedIcon)`
  position: relative;
  background: ${({ theme, disabled }) =>
    !disabled && theme.checkbox.selected.enabled.container.fill};
  border: none;
`;

const Checkmark = styled(CheckedSvg)<{ disabled: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: ${({ theme, disabled }) =>
    disabled && theme.checkbox.selected.disabled.icon.opacity};
`;

export interface ICheckboxProps
  extends Pick<CheckboxProps, 'checked' | 'onChange' | 'disabled'> {}

export const Checkbox = ({
  checked,
  disabled = false,
  ...props
}: ICheckboxProps) => (
  <CheckboxContainer disabled={disabled}>
    <ControlContainer>
      <Control
        checked={checked}
        disabled={disabled}
        disableRipple
        checkedIcon={<CheckedIcon className="checkmark" disabled={disabled} />}
        icon={<UncheckedIcon className="checkmark" disabled={disabled} />}
        {...props}
      />
      {checked && <Checkmark disabled={disabled} />}
    </ControlContainer>
  </CheckboxContainer>
);
